/**
 * Shared framer-motion variants for the v2 landing redesign.
 *
 * Every section imports its motion vocabulary from here — nothing invents
 * its own. Standard ease is [0.16, 0.84, 0.44, 1] for fades/slides; springs
 * use stiffness 120 / damping 25 (softened for calm JP-B2B gravitas).
 *
 * framer-motion already respects prefers-reduced-motion for whileInView /
 * animate built-ins. ADDITIONALLY, every repeat:Infinity loop and every
 * count-up / gauge must be gated behind usePrefersReducedMotion() (see
 * ./primitives) and render its end-state statically.
 */
import type { Variants, Transition } from 'framer-motion';

/** Project-standard easing curve for reveals and slides. */
export const EASE_STANDARD: [number, number, number, number] = [0.16, 0.84, 0.44, 1];

/** Softened spring for hovers, tilts and timeline draws. */
export const SPRING_SOFT: Transition = { type: 'spring', stiffness: 120, damping: 25 };

// ── Reveals ──────────────────────────────────────────────────────────────────

/** Workhorse reveal — content rises 30px into place. */
export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE_STANDARD } },
};

/** Wedge-seam slide from the start side (x:-40). Physical x — won't RTL-flip. */
export const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: EASE_STANDARD } },
};

/** Wedge-seam slide from the end side (x:+40). Physical x — won't RTL-flip. */
export const fadeInRight: Variants = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: EASE_STANDARD } },
};

/** Card / chip emphasis — keenez wedge card, comparison ✓✓, pricing Global, copilot chip. */
export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.55, ease: EASE_STANDARD } },
};

// ── Stagger ────────────────────────────────────────────────────────────────

/** Parent drives timing; children stay bare (just `variants={fadeInUp}`). */
export const staggerContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

/** Slower stagger for heavier hero / pillar grids. */
export const staggerSlow: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14 } },
};

// ── Draws (timeline / seam / gauge) — pair with origin-start / origin-top ────

/** Horizontal draw. Apply `className="origin-start"` (or origin-left) on the node. */
export const drawX: Variants = {
  hidden: { scaleX: 0 },
  visible: { scaleX: 1, transition: { duration: 0.7, ease: EASE_STANDARD } },
};

/** Vertical draw. Apply `className="origin-top"` on the node. */
export const drawY: Variants = {
  hidden: { scaleY: 0 },
  visible: { scaleY: 1, transition: { duration: 0.7, ease: EASE_STANDARD } },
};

// ── Viewport ─────────────────────────────────────────────────────────────────

/** Reveal once, 80px before the element fully enters the viewport. */
export const sectionViewport = { once: true, margin: '-80px' as const };
